import { io, Socket } from 'socket.io-client'
import toast from 'react-hot-toast'

interface WebSocketEvents {
  'agent:status': (data: AgentStatusUpdate) => void
  'revenue:update': (data: RevenueUpdate) => void
  'system:health': (data: SystemHealthUpdate) => void
  'report:generated': (data: ReportGeneratedEvent) => void
  'error': (error: ErrorEvent) => void
}

interface AgentStatusUpdate {
  agentId: string
  status: 'running' | 'stopped' | 'error'
  lastSync: string
  dataExtracted?: number
  error?: string
}

interface RevenueUpdate {
  storeId: string
  date: string
  revenue: number
  grossProfit: number
  timestamp: string
}

interface SystemHealthUpdate {
  service: string
  status: boolean
  message?: string
}

interface ReportGeneratedEvent {
  reportId: string
  reportType: string
  storeId: string
  date: string
  status: 'completed' | 'failed'
}

interface ErrorEvent {
  code: string
  message: string
  timestamp: string
}

class WebSocketService {
  private socket: Socket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 3000
  private listeners: Map<string, Set<Function>> = new Map()

  connect(token?: string) {
    if (this.socket?.connected) {
      return
    }

    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8100'

    this.socket = io(wsUrl, {
      transports: ['websocket'],
      auth: {
        token: token || localStorage.getItem('authToken')
      },
      reconnection: true,
      reconnectionDelay: this.reconnectDelay,
      reconnectionAttempts: this.maxReconnectAttempts
    })

    this.setupEventHandlers()
  }

  private setupEventHandlers() {
    if (!this.socket) return

    this.socket.on('connect', () => {
      console.log('WebSocket connected')
      this.reconnectAttempts = 0
      toast.success('Real-time updates connected', { duration: 2000 })
    })

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason)
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, attempt reconnect
        this.socket?.connect()
      }
    })

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error)
      this.reconnectAttempts++

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        toast.error('Unable to connect to real-time updates')
      }
    })

    // Agent status updates
    this.socket.on('agent:status', (data: AgentStatusUpdate) => {
      this.emit('agent:status', data)

      if (data.status === 'error') {
        toast.error(`Agent ${data.agentId} error: ${data.error}`)
      }
    })

    // Revenue updates from GUI Agent
    this.socket.on('revenue:update', (data: RevenueUpdate) => {
      this.emit('revenue:update', data)
      console.log('Revenue update received:', data)
    })

    // System health updates
    this.socket.on('system:health', (data: SystemHealthUpdate) => {
      this.emit('system:health', data)

      if (!data.status) {
        toast.error(`${data.service} is experiencing issues`)
      }
    })

    // Report generation events
    this.socket.on('report:generated', (data: ReportGeneratedEvent) => {
      this.emit('report:generated', data)

      if (data.status === 'completed') {
        toast.success(`${data.reportType} report ready`)
      } else {
        toast.error(`${data.reportType} report generation failed`)
      }
    })

    // Error handling
    this.socket.on('error', (error: ErrorEvent) => {
      console.error('WebSocket error:', error)
      this.emit('error', error)
    })
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
    this.listeners.clear()
  }

  // Subscribe to events
  on<K extends keyof WebSocketEvents>(
    event: K,
    callback: WebSocketEvents[K]
  ): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }

    this.listeners.get(event)!.add(callback as Function)

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(event)
      if (callbacks) {
        callbacks.delete(callback as Function)
      }
    }
  }

  // Emit events to local listeners
  private emit(event: string, data: any) {
    const callbacks = this.listeners.get(event)
    if (callbacks) {
      callbacks.forEach(callback => callback(data))
    }
  }

  // Send message to server
  send(event: string, data: any) {
    if (this.socket?.connected) {
      this.socket.emit(event, data)
    } else {
      console.warn('WebSocket not connected, cannot send:', event, data)
    }
  }

  // Request immediate data refresh
  requestRefresh(type: 'agent' | 'revenue' | 'all') {
    this.send('request:refresh', { type })
  }

  // Subscribe to specific store updates
  subscribeToStore(storeId: string) {
    this.send('subscribe:store', { storeId })
  }

  // Unsubscribe from store updates
  unsubscribeFromStore(storeId: string) {
    this.send('unsubscribe:store', { storeId })
  }

  isConnected(): boolean {
    return this.socket?.connected || false
  }
}

// Create singleton instance
const wsService = new WebSocketService()

// Auto-connect when token is available
if (localStorage.getItem('authToken')) {
  wsService.connect()
}

export default wsService